/**
 * Pure metric derivation functions for the Project Dashboard.
 * No side effects, no network calls — all computation is deterministic.
 */

import type { ZohoTask, ZohoMilestone, ZohoProject, ZohoUser } from "../types/zoho.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TaskSummary {
	total: number;
	open: number;
	inProgress: number;
	completed: number;
	overdue: number;
	percentComplete: number;
}

export interface TeamWorkload {
	userId: string;
	userName: string;
	total: number;
	byStatus: Record<string, number>;
}

export interface ProjectDuration {
	startDate: string | null;
	endDate: string | null;
	daysElapsed: number;
	daysRemaining: number | null;
	percentElapsed: number | null;
	estimatedEnd: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Strips time component and returns a date at midnight UTC. Returns an invalid Date if input is malformed. */
function toDateOnly(dateStr: string): Date {
	// Handle ISO timestamps (e.g. "2024-03-21T10:00:00Z") by taking only the date portion
	const datePart = dateStr.split("T")[0]!;
	const parts = datePart.split("-").map(Number);
	if (parts.length !== 3 || parts.some((n) => isNaN(n))) {
		return new Date(NaN);
	}
	const [year, month, day] = parts as [number, number, number];
	return new Date(Date.UTC(year, month - 1, day));
}

/** Returns today at midnight UTC. */
function todayUtc(): Date {
	const now = new Date();
	return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

/** Difference in whole days between two UTC-midnight dates. Returns 0 if either date is invalid. */
function daysBetween(a: Date, b: Date): number {
	const aMs = a.getTime();
	const bMs = b.getTime();
	if (isNaN(aMs) || isNaN(bMs)) return 0;
	const msPerDay = 86_400_000;
	return Math.round((bMs - aMs) / msPerDay);
}

/** Status names that indicate "in progress" work. */
const IN_PROGRESS_STATUSES = new Set([
	"In Progress",
	"In Progress with Dev",
	"In Progress with CA Team",
	"With Client",
]);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns true if the task's endDate is strictly in the past AND the task is
 * not completed. A task due today is not overdue. Tasks with no endDate are
 * never overdue.
 */
export function isOverdue(task: ZohoTask): boolean {
	if (task.completed || task.endDate == null) return false;
	const end = toDateOnly(task.endDate);
	const today = todayUtc();
	return end.getTime() < today.getTime();
}

/**
 * Derives aggregate task counts and completion percentage from a task list.
 */
export function computeTaskSummary(tasks: ZohoTask[]): TaskSummary {
	if (tasks.length === 0) {
		return { total: 0, open: 0, inProgress: 0, completed: 0, overdue: 0, percentComplete: 0 };
	}

	let completed = 0;
	let inProgress = 0;
	let open = 0;
	let overdue = 0;

	for (const task of tasks) {
		if (task.completed) {
			completed++;
		} else if (IN_PROGRESS_STATUSES.has(task.statusName)) {
			inProgress++;
		} else {
			open++;
		}

		if (isOverdue(task)) {
			overdue++;
		}
	}

	const rawPct = tasks.length > 0 ? Math.round((completed / tasks.length) * 100) : 0;
	const percentComplete = isNaN(rawPct) ? 0 : rawPct;

	return {
		total: tasks.length,
		open,
		inProgress,
		completed,
		overdue,
		percentComplete,
	};
}

/**
 * Groups tasks by assignee, producing a workload entry per user who has at
 * least one task. Users with zero tasks are excluded. Unassigned tasks are
 * not attributed to any user.
 */
export function computeTeamWorkload(tasks: ZohoTask[], _users: ZohoUser[]): TeamWorkload[] {
	if (tasks.length === 0) return [];

	const map = new Map<string, TeamWorkload>();

	for (const task of tasks) {
		for (let i = 0; i < task.assigneeIds.length; i++) {
			const userId = task.assigneeIds[i]!;
			const userName = task.assigneeNames[i] ?? "Unknown";

			let entry = map.get(userId);
			if (!entry) {
				entry = { userId, userName, total: 0, byStatus: {} };
				map.set(userId, entry);
			}

			entry.total++;
			entry.byStatus[task.statusName] = (entry.byStatus[task.statusName] ?? 0) + 1;
		}
	}

	return Array.from(map.values());
}

/**
 * Computes project duration metrics: elapsed days, remaining days, and
 * percentage elapsed. Falls back to the latest milestone endDate when the
 * project endDate is null.
 */
export function computeProjectDuration(
	project: ZohoProject,
	milestones: ZohoMilestone[],
): ProjectDuration {
	const startDate = project.startDate ?? null;
	const projectEnd = project.endDate ?? null;

	// Determine estimated end: prefer project endDate, fall back to latest milestone
	let estimatedEnd = projectEnd;
	if (!estimatedEnd && milestones.length > 0) {
		let latest: string | null = null;
		for (const m of milestones) {
			if (m.endDate && (!latest || m.endDate > latest)) {
				latest = m.endDate;
			}
		}
		estimatedEnd = latest;
	}

	const today = todayUtc();

	// Days elapsed since start
	let daysElapsed = 0;
	if (startDate) {
		const start = toDateOnly(startDate);
		const raw = daysBetween(start, today);
		daysElapsed = isNaN(raw) ? 0 : Math.max(0, raw);
	}

	// Days remaining until end
	let daysRemaining: number | null = null;
	if (estimatedEnd) {
		const end = toDateOnly(estimatedEnd);
		const raw = daysBetween(today, end);
		daysRemaining = isNaN(raw) ? 0 : Math.max(0, raw);
	}

	// Percentage elapsed
	let percentElapsed: number | null = null;
	if (startDate && estimatedEnd) {
		const start = toDateOnly(startDate);
		const end = toDateOnly(estimatedEnd);
		const totalDays = daysBetween(start, end);
		if (totalDays > 0) {
			const raw = Math.round((daysElapsed / totalDays) * 100);
			percentElapsed = isNaN(raw) ? 0 : Math.min(100, raw);
		}
	}

	return {
		startDate,
		endDate: projectEnd,
		daysElapsed,
		daysRemaining,
		percentElapsed,
		estimatedEnd,
	};
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

/** Escapes a CSV field value — wraps in double-quotes if it contains commas, quotes, or newlines. */
function escapeCsvField(value: string): string {
	if (value.includes(",") || value.includes('"') || value.includes("\n")) {
		return `"${value.replace(/"/g, '""')}"`;
	}
	return value;
}

/**
 * Generates a CSV string for a list of tasks.
 * Always includes a header row. Returns header-only for an empty list.
 */
export function generateTasksCsv(tasks: ZohoTask[]): string {
	const headers = ["Name", "Status", "Priority", "Assignee", "Start Date", "End Date", "Completed", "Task List"];
	const lines = [headers.join(",")];

	for (const task of tasks) {
		const row = [
			escapeCsvField(task.name),
			escapeCsvField(task.statusName),
			escapeCsvField(task.priority),
			escapeCsvField(task.assigneeNames.join("; ")),
			task.startDate ?? "",
			task.endDate ?? "",
			task.completed ? "Yes" : "No",
			escapeCsvField(task.taskListName),
		];
		lines.push(row.join(","));
	}

	return lines.join("\n");
}

/**
 * Generates a CSV string for a list of milestones.
 * Always includes a header row. Returns header-only for an empty list.
 */
export function generateMilestonesCsv(milestones: ZohoMilestone[]): string {
	const headers = ["Name", "Status", "Owner", "Start Date", "End Date", "Completed Date"];
	const lines = [headers.join(",")];

	for (const milestone of milestones) {
		const row = [
			escapeCsvField(milestone.name),
			milestone.completed ? "Completed" : "Open",
			escapeCsvField(milestone.ownerName),
			milestone.startDate ?? "",
			milestone.endDate ?? "",
			milestone.completedDate ?? "",
		];
		lines.push(row.join(","));
	}

	return lines.join("\n");
}
